<template>
  <div class="able-page">
    <MainNavbar />

    <!-- Hero image (top) -->
    <figure class="able-hero-visual">
      <img
        :src="mainImage"
        alt="Kenyan paralympic athletes and adaptive sport"
        class="able-hero-visual__img"
        decoding="async"
        fetchpriority="high"
      />
    </figure>

    <!-- Title & tagline -->
    <header class="able-hero-text">
      <p class="able-kicker">
        <i class="fa-solid fa-universal-access" aria-hidden="true" />
        Kenyan paralympic sport
      </p>
      <h1 class="able-title">The Able</h1>
      <p class="able-tagline">Creating a future for the special in Africa</p>
      <p class="able-intro">
        Kenya’s paralympic niche is full of talent—but athletes face scarce adaptive equipment,
        too few specialist trainers, and limited tournament pathways. We partner with
        <strong>special schools</strong> to change that.
      </p>
    </header>

    <!-- Irregular image mosaic -->
    <section class="able-section able-section--mosaic" aria-label="Sport in action">
      <div class="able-section__inner">
        <AbleGalleryGrid
          :basketball-src="basketballImage"
          :runner-src="runnerImage"
          :racer-src="racerImage"
        />
      </div>
    </section>

    <!-- Challenges -->
    <section class="able-features" aria-labelledby="able-challenges-heading">
      <div class="able-features__head">
        <p class="able-kicker">The gap</p>
        <h2 id="able-challenges-heading" class="able-section__title">What holds talent back</h2>
        <p class="able-section__desc">
          Three barriers we hear from schools, parents, and athletes across Kenya.
        </p>
      </div>
      <div class="able-features__grid">
        <AbleFeatureCard
          v-for="item in challenges"
          :key="item.id"
          :item="item"
        />
      </div>
    </section>

    <!-- Approach -->
    <section class="able-module" aria-labelledby="able-approach-heading">
      <div class="able-module__inner">
        <p class="able-kicker">Our approach</p>
        <h2 id="able-approach-heading" class="able-section__title">Partner with special schools</h2>
        <p class="able-module__lead">
          The Able works <strong>with</strong> special schools—not around them—to grow impairment sport
          from the classroom and playground outward.
        </p>
        <ul class="able-checklist">
          <li v-for="(point, idx) in schoolPoints" :key="idx">
            <i class="fa-solid fa-circle-check" aria-hidden="true" />
            {{ point }}
          </li>
        </ul>
      </div>
    </section>

    <!-- Four pillars -->
    <section class="able-features able-features--alt" aria-labelledby="able-pillars-heading">
      <div class="able-features__head">
        <p class="able-kicker">Programme</p>
        <h2 id="able-pillars-heading" class="able-section__title">How The Able helps</h2>
      </div>
      <div class="able-features__grid">
        <AbleFeatureCard
          v-for="item in pillars"
          :key="item.id"
          :item="item"
        />
      </div>
    </section>

    <!-- Equipment store -->
    <section class="able-section" aria-labelledby="able-store-heading">
      <div class="able-section__inner able-section__inner--narrow">
        <AbleActionPanel
          kicker="Adaptive equipment"
          icon="fa-solid fa-store"
          title="Paralympic equipment store"
          lead="Browse wheelchairs, sport chairs, prosthetics, protective gear, and training aids curated for adaptive sport in Kenya—on IPA Store."
          :bullets="[
            'Items sourced for school and community budgets',
            'Guidance on sizing and sport-specific fit',
            'Delivery and support through IPA’s store network',
          ]"
          primary-label="Explore Store"
          primary-icon="fa-solid fa-arrow-right"
          :primary-to="storePath"
          footnote="Store catalogue for dedicated paralympic categories is expanding—explore adaptive and sports gear today."
        />
      </div>
    </section>

    <!-- Trainer registration -->
    <section class="able-section able-section--alt" aria-labelledby="able-trainer-heading">
      <div class="able-section__inner able-section__inner--narrow">
        <AbleActionPanel
          kicker="Coaches & therapists"
          icon="fa-solid fa-user-nurse"
          title="Register as a trainer"
          lead="Are you a coach, physiotherapist, or PE specialist working with impaired athletes? Join our trainer network and get matched to partner schools."
          :bullets="trainerPerks"
          primary-label="Register as trainer"
          primary-icon="fa-solid fa-id-card"
          secondary-label="Learn about partnerships"
          secondary-icon="fa-solid fa-circle-info"
          highlight
          footnote="Trainer verification and school matching—API onboarding coming soon."
          @primary="onTrainerRegister"
          @secondary="scrollToSchools"
        />
      </div>
    </section>

    <!-- Tournaments CTA -->
    <section class="able-cta" aria-labelledby="able-cta-heading">
      <p class="able-kicker able-kicker--light">
        <i class="fa-solid fa-medal" aria-hidden="true" />
        Experience through play
      </p>
      <h2 id="able-cta-heading" class="able-cta__title">More tournaments. More confidence.</h2>
      <p class="able-cta__body">
        Fixtures and festivals give athletes the reps they need—so talent shows up before the
        national spotlight, not only after it.
      </p>
      <router-link :to="{ name: 'MatchesHub' }" class="btn primary large">
        <i class="fa-solid fa-people-group" aria-hidden="true" />
        Explore Match Hub
      </router-link>
    </section>

    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import MainFooter from "@/components/main/headernfooter/Footer.vue";
import AbleGalleryGrid from "@/components/theable/AbleGalleryGrid.vue";
import AbleFeatureCard from "@/components/theable/AbleFeatureCard.vue";
import AbleActionPanel from "@/components/theable/AbleActionPanel.vue";
import mainImage from "@/assets/images/paralympics/main.png";
import basketballImage from "@/assets/images/paralympics/basketball.jpg";
import runnerImage from "@/assets/images/paralympics/runner.jpg";
import racerImage from "@/assets/images/paralympics/racer.png";
import {
  THE_ABLE_CHALLENGES,
  THE_ABLE_PILLARS,
  THE_ABLE_SCHOOL_POINTS,
  THE_ABLE_TRAINER_PERKS,
  THE_ABLE_STORE_PATH,
} from "@/routes/theable/constants";

export default {
  name: "TheAbleIntro",
  components: {
    MainNavbar,
    MainFooter,
    AbleGalleryGrid,
    AbleFeatureCard,
    AbleActionPanel,
  },
  data() {
    return {
      mainImage,
      basketballImage,
      runnerImage,
      racerImage,
      challenges: THE_ABLE_CHALLENGES,
      pillars: THE_ABLE_PILLARS,
      schoolPoints: THE_ABLE_SCHOOL_POINTS,
      trainerPerks: THE_ABLE_TRAINER_PERKS,
      storePath: THE_ABLE_STORE_PATH,
    };
  },
  methods: {
    onTrainerRegister() {
      window.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: {
            title: "Coming soon",
            message:
              "Trainer registration for The Able will open shortly. Thank you for wanting to coach Kenya’s paralympic athletes.",
            type: "info",
          },
        }),
      );
    },
    scrollToSchools() {
      const el = document.getElementById("able-approach-heading");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
};
</script>

<style scoped src="./theAbleIntro.css"></style>
